/**
 * Weapon System - Multiple weapon types for Kid Kelly's Retro Blaster
 */

export enum WeaponType {
  WAVE_CANNON = 1,     // Original FM Wave (cyan)
  WAVE_SIGNAL = 2,     // Single thin, fast, piercing beam (magenta)
  DUAL_CANNON = 3,     // Two parallel shots (green)
  MACHINE_GUN = 4,     // Rapid single bullets, low damage (yellow)
  SHOTGUN_BLAST = 5,   // Wide spread of 5-7 projectiles (orange)
  FM_SUPER_BLAST = 6   // Large slow orb, heavy damage, small AoE (purple)
}

export interface WeaponConfig {
  name: string;
  fireRate: number;  // Milliseconds between shots
  damage: number;
  color: string;
  speed: number;
  size: number;
  piercing?: number; // number of enemies projectile can pierce
  spread?: number;  // For shotgun
  count?: number;   // For shotgun/multi-shot
}

export const WEAPON_CONFIGS: Record<WeaponType, WeaponConfig> = {
  [WeaponType.WAVE_CANNON]: {
    name: 'Love Wave',
    fireRate: 400,  // Slower starting weapon
    damage: 15,     // Lower damage for starting weapon
    color: '#00ffff',
    speed: 5,
    size: 40
  },
  [WeaponType.WAVE_SIGNAL]: {
    name: 'Light Laser',
    fireRate: 200,  // Faster than starting
    damage: 12,     // Slightly lower damage but piercing
    color: '#ff0080',
    speed: 8,
    size: 4,
    piercing: 2     // Reduced piercing for balance
  },
  [WeaponType.DUAL_CANNON]: {
    name: 'Rainbow Ray',
    fireRate: 350,  // Moderate fire rate
    damage: 18,     // Better damage than starting
    color: '#00ff41',
    speed: 6,
    size: 8,
    count: 2
  },
  [WeaponType.MACHINE_GUN]: {
    name: 'Hope Burst',
    fireRate: 100,  // Very fast
    damage: 8,      // Low damage but high fire rate
    color: '#ffff00',
    speed: 7,
    size: 6
  },
  [WeaponType.SHOTGUN_BLAST]: {
    name: 'Unity Spread',
    fireRate: 500,  // Slower but area damage
    damage: 10,     // Moderate damage per pellet
    color: '#ff4500',
    speed: 5,
    size: 8,
    spread: 25,     // Tighter spread
    count: 5        // Fewer pellets
  },
  [WeaponType.FM_SUPER_BLAST]: {
    name: 'Harmony Blast',
    fireRate: 1000, // Slow but powerful
    damage: 80,     // Very high damage
    color: '#8a2be2',
    speed: 4,
    size: 30
  }
};

export interface Projectile {
  x: number;
  y: number;
  vx: number;
  vy: number;
  width: number;
  height: number;
  damage: number;
  color: string;
  active: boolean;
  friendly: boolean;
  piercing?: number; // number of enemies it can pierce through
  weaponType: WeaponType;
  age?: number;
  maxAge?: number;
}

export class WeaponSystem {
  private currentWeapon: WeaponType = WeaponType.WAVE_CANNON;
  private lastFireTime: number = 0;
  private fireRateModifier: number = 1.0; // 1.0 = normal, 0.5 = twice as fast

  public getCurrentWeapon(): WeaponType {
    return this.currentWeapon;
  }

  public switchWeapon(weaponType: WeaponType): void {
    if (weaponType >= 1 && weaponType <= 6) {
      this.currentWeapon = weaponType;
      console.log('WeaponSystem: Switched to weapon type', weaponType, 'Config:', WEAPON_CONFIGS[weaponType].name);
    }
  }

  public cycleWeapon(): void {
    this.currentWeapon = (this.currentWeapon % 6) + 1;
  }

  public setFireRateModifier(modifier: number): void {
    this.fireRateModifier = modifier;
  }

  public canFire(): boolean {
    const config = WEAPON_CONFIGS[this.currentWeapon];
    const now = Date.now();
    const adjustedFireRate = config.fireRate * this.fireRateModifier;
    return now - this.lastFireTime >= adjustedFireRate;
  }

  public fire(playerX: number, playerY: number): Projectile[] {
    if (!this.canFire()) {
      return [];
    }

    this.lastFireTime = Date.now();
    const config = WEAPON_CONFIGS[this.currentWeapon];
    const projectiles: Projectile[] = [];
    
    console.log('Firing weapon:', this.currentWeapon, 'Name:', config.name, 'Color:', config.color);

    switch (this.currentWeapon) {
      case WeaponType.WAVE_CANNON:
        const waveCannon = this.createProjectile(playerX, playerY, 0, config);
        waveCannon.maxAge = 2000; // 2 second lifetime for growth effect
        waveCannon.age = 0;
        projectiles.push(waveCannon);
        break;

      case WeaponType.WAVE_SIGNAL:
        const waveSignal = this.createProjectile(playerX, playerY, 0, config);
        waveSignal.maxAge = 2000; // 2 second lifetime for growth effect
        waveSignal.age = 0;
        projectiles.push(waveSignal);
        break;

      case WeaponType.DUAL_CANNON:
        const rainbow1 = this.createProjectile(playerX - 15, playerY, 0, config);
        rainbow1.maxAge = 1500; // 1.5 second lifetime for growth effect
        rainbow1.age = 0;
        projectiles.push(rainbow1);
        
        const rainbow2 = this.createProjectile(playerX + 15, playerY, 0, config);
        rainbow2.maxAge = 1500; // 1.5 second lifetime for growth effect
        rainbow2.age = 0;
        projectiles.push(rainbow2);
        break;

      case WeaponType.MACHINE_GUN:
        const hopeBurst = this.createProjectile(playerX, playerY, 0, config);
        hopeBurst.maxAge = 2000; // 2 second lifetime for growth effect
        hopeBurst.age = 0;
        projectiles.push(hopeBurst);
        break;

      case WeaponType.SHOTGUN_BLAST:
        const spread = config.spread || 30;
        const count = config.count || 7;
        const angleStep = spread / (count - 1);
        const startAngle = -spread / 2;
        
        for (let i = 0; i < count; i++) {
          const angle = startAngle + (angleStep * i);
          const unitySpread = this.createProjectile(playerX, playerY, angle, config);
          unitySpread.maxAge = 2000; // 2 second lifetime for growth effect
          unitySpread.age = 0;
          projectiles.push(unitySpread);
        }
        break;

      case WeaponType.FM_SUPER_BLAST:
        const superBlast = this.createProjectile(playerX, playerY, 0, config);
        superBlast.maxAge = 2000; // 2 second lifetime
        superBlast.age = 0;
        projectiles.push(superBlast);
        break;
    }

    return projectiles;
  }

  private createProjectile(x: number, y: number, angle: number, config: WeaponConfig): Projectile {
    const angleRad = (angle * Math.PI) / 180;
    const vx = Math.sin(angleRad) * config.speed;
    const vy = -Math.cos(angleRad) * config.speed;

    return {
      x,
      y,
      vx,
      vy,
      width: config.size,
      height: config.size,
      damage: config.damage,
      color: config.color,
      active: true,
      friendly: true,
      piercing: config.piercing || 0,
      weaponType: this.currentWeapon
    };
  }

  public getWeaponName(): string {
    return WEAPON_CONFIGS[this.currentWeapon].name;
  }
}
